import { UnknownAction } from '@reduxjs/toolkit';
import {
  ConfigurationType,
  ExistingSystemType,
  ProjectSettingCodeType
} from 'data/types';
import {
  clearComparisonRemovedUniqueIds,
  setActiveDuctedSystem,
  setActiveSelectedSystem,
  setSelectedDuctedSystems,
  setSelectedSystems,
  setSystemsToCompare
} from 'features/systems';
import { setComparisonTableData } from 'features/ui';
import { CompositionPicks } from 'utils/system-composition-types';

const OMITTED_COMPONENT_KEYS = new Set([
  'analyzing',
  'position',
  'levelUniqueId',
  'imageAttachmentIds',
  'specSources',
  'manufactureDate'
]);

const OPTION_PROJECT_SETTING_CODES: ProjectSettingCodeType[] = [
  'system_preference',
  'coverage_area',
  'aesthetic_preference',
  'performance_preference',
  'value_preference',
  'install_ease_preference',
  'noise_preference',
  'resilience_preference',
  'maintenance_preference',
  'desired_cooling',
  'desired_heating',
  'desired_system_types',
  'sizing_preference',
  'system_fuel_intent',
  'replacing_with_different_equipment',
  'consider_alternate_fuel_types',
  'wifi_preference',
  'desired_efficiency_tier',
  'thermostat_preference',
  'ductwork',
  'duct_system_type',
  'heating',
  'cooling',
  'square_footage',
  'insulation_level',
  'number_of_floors'
];

export type RecommendationSelectionSnapshot = {
  wantIds?: number[];
  haveId?: number | null;
  refrigerant?: string | null;
  age?: string | null;
  useRecommendations?: boolean;
  virtualCodes?: string[];
  virtualFuel?: string | null;
  tonnage?: unknown;
};

type StructureSpaceInput = {
  uniqueId: string;
  BTUs?: unknown;
  attributes: Array<{ code: string; value?: unknown }>;
};

type StructureLevelInput = {
  sections: Array<{
    spaces: StructureSpaceInput[];
  }>;
};

type ProjectSettingInput = {
  code: string;
  value?: unknown;
};

export type ProjectOptionInputs = {
  projectSettings: ProjectSettingInput[];
  zipCode?: string | null;
  totalBTUs: { heating: number; cooling: number };
  supplementalMiniSplitSpaces: string[];
};

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((entry) => stableValue(entry));
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return Object.keys(record)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = stableValue(record[key]);
        return acc;
      }, {});
  }
  return value;
};

const stableStringify = (value: unknown): string =>
  JSON.stringify(stableValue(value));

export function structureSelectionSignature(
  structure: StructureLevelInput[]
): string {
  return JSON.stringify(
    structure.map((level) =>
      level.sections.map((section) =>
        section.spaces.map((space) => ({
          id: space.uniqueId,
          BTUs: space.BTUs,
          attributes: space.attributes
            .filter(
              (attribute) => attribute.code !== 'preferred_air_handler_type'
            )
            .map((attribute) => ({
              code: attribute.code,
              value: attribute.value
            }))
        }))
      )
    )
  );
}

export function existingEquipmentSignature(
  systems: ExistingSystemType[]
): string {
  const normalized = [...systems]
    .sort((left, right) => left.uniqueId.localeCompare(right.uniqueId))
    .map((system) => ({
      uniqueId: system.uniqueId,
      type: system.type,
      components: [...system.components]
        .sort((left, right) => left.uniqueId.localeCompare(right.uniqueId))
        .map((component) =>
          Object.entries(component).reduce<Record<string, unknown>>(
            (acc, [key, value]) => {
              if (!OMITTED_COMPONENT_KEYS.has(key)) {
                acc[key] = value;
              }
              return acc;
            },
            {}
          )
        )
    }));
  return stableStringify(normalized);
}

export function configurationOptionSignature(
  configurations: ConfigurationType[]
): string {
  return stableStringify(
    configurations.map((config) => ({
      uniqueId: config.uniqueId,
      type: config.type,
      condensers: config.condensers.map((condenser) => ({
        uniqueId: condenser.uniqueId,
        type: condenser.type,
        positionX: condenser.position?.x ?? null,
        positionY: condenser.position?.y ?? null,
        levelUniqueId: condenser.levelUniqueId ?? null,
        heightOffGround: condenser.heightOffGround ?? null,
        placementType: condenser.placementType ?? null,
        zones: condenser.zones.map((zone) => ({
          uniqueId: zone.uniqueId,
          type: zone.type,
          airHandlerType: zone.airHandler?.airHandlerTypeCode ?? null,
          items: zone.zoneItems.map((item) => ({
            id: item.structureItemId,
            heating: item.BTUs?.heating ?? null,
            cooling: item.BTUs?.cooling ?? null
          }))
        }))
      }))
    }))
  );
}

export function compositionPicksSignature(
  picks: Record<string, CompositionPicks>
): string {
  return stableStringify(picks);
}

export function projectOptionInputsSignature(
  input: ProjectOptionInputs
): string {
  const settings = OPTION_PROJECT_SETTING_CODES.map((code) => ({
    code,
    value:
      input.projectSettings.find((setting) => setting.code === code)?.value ??
      null
  }));
  return stableStringify({
    settings,
    zipCode: input.zipCode ?? null,
    totalBTUs: input.totalBTUs,
    supplementalMiniSplitSpaces: [...input.supplementalMiniSplitSpaces].sort()
  });
}

export function recommendationSelectionsSignature(
  selections: Readonly<Record<string, RecommendationSelectionSnapshot | null>>
): string {
  const normalized = Object.keys(selections)
    .sort()
    .reduce<Record<string, unknown>>((acc, systemId) => {
      const entry = selections[systemId];
      acc[systemId] = entry
        ? {
            wantIds: [...(entry.wantIds ?? [])].sort(
              (left, right) => left - right
            ),
            haveId: entry.haveId ?? null,
            refrigerant: entry.refrigerant ?? null,
            age: entry.age ?? null,
            useRecommendations: entry.useRecommendations ?? null,
            virtualCodes: [...(entry.virtualCodes ?? [])].sort(),
            virtualFuel: entry.virtualFuel ?? null,
            tonnage: entry.tonnage ?? null
          }
        : null;
      return acc;
    }, {});
  return stableStringify(normalized);
}

export function resetInvalidatedSystemSelections(
  dispatch: (action: UnknownAction) => void
) {
  dispatch(setSelectedSystems([]));
  dispatch(setSelectedDuctedSystems([]));
  dispatch(setActiveSelectedSystem(0));
  dispatch(setActiveDuctedSystem(0));
  dispatch(setSystemsToCompare([]));
  dispatch(clearComparisonRemovedUniqueIds());
  dispatch(setComparisonTableData(null));
}
